import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { fetchProfile, Profile } from '../lib/profile';
import { getCurrentUser } from '../lib/auth';
import { supabase } from '../lib/supabase';

interface UserProfileContextType {
  profile: Profile | null;
  isProfileLoading: boolean;
  setProfile: React.Dispatch<React.SetStateAction<Profile | null>>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export function UserProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const loadProfile = async () => {
    const requestId = ++requestIdRef.current;
    setIsProfileLoading(true);
    try {
      const user = await getCurrentUser();
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;

      if (user) {
        const prof = await fetchProfile(user.id);
        if (!isMountedRef.current || requestId !== requestIdRef.current) return;
        setProfile(prof);
      } else {
        setProfile(null);
      }
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) return;

      if (error && error.message !== 'Auth session missing!') {
        console.error('Error loading user profile:', error);
      }
      setProfile(null);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setIsProfileLoading(false);
      }
    }
  };

  // Load profile on mount
  useEffect(() => {
    isMountedRef.current = true;
    loadProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event) => {
      loadProfile();
    });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <UserProfileContext.Provider value={{ profile, isProfileLoading, setProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error('useUserProfile must be used within a UserProfileProvider');
  return ctx;
}
